/* Approval flow is quite complex and can be confusing. There are two main flows:
1. Bulk Approval Flow
2. Single Approval Flow

Both flows have to account for product sku and parent sku mapping.

Generally single approval flow always has a dialog to indicate action meaning a dialog always shows even if its a simple approve.
If there is a mapping already exisitng, etc...

Bulk approval tries to skip certain things to speed up the process and require less user interaction.
For example:
- If a mapping already exists, it will just approve the product. 
- If there is no dupe products and no mapping, it will just approve the product.

If there exists a mapping we generally just take it and go. 
*/

import React, {useEffect, useState} from "react";
import {approveProduct as utilApproveProduct, getProduct as utilGetProduct, utilAddInventory, utilCheckInventory, utilGetPartnerSettings, utilSearchRetailerCatalog, isNullOrEmpty} from "../../utils/utils";
import {Button, Dialog, Grid, Typography, withTheme} from "@material-ui/core";
import clsx from "clsx";
import {useGlobalStyles} from "../../utils/styles";
import ContentLoader from "../../../general/content_loader";
import {ProductColumn} from "../../containers/catalog/components/catalog_columns";
import Auth from "../../../auth";
import EnhancedTextField from "../EnhancedTextField";

export const compareSkus = (sku1, sku2, caseSensitive) => {
    if (!sku1 || !sku2) return sku1 === sku2;
    return caseSensitive ? sku1 === sku2 : sku1.toUpperCase() === sku2.toUpperCase();
}

const updateInventory = async (product, changedSku, inventory, partnerSettings = { caseSensitive: false }) => {
    let usedSku = changedSku === "" ? product.sku : changedSku;
    let originalSku = product.sku;
    
    // If case sensitivity is off, uppercase the SKUs
    if (!partnerSettings.caseSensitive) {
        usedSku = usedSku ? usedSku.toUpperCase() : usedSku;
        originalSku = originalSku ? originalSku.toUpperCase() : originalSku;
    }
    
    const runStatus = await utilAddInventory(product.supplierId, usedSku, originalSku, inventory);
    if(runStatus.errorMessage) {
        return {
            success: false,
            data: null,
            error: runStatus.errorMessage
        };
    }
    else {
        return {
            success: true,
            data: null,
            error: null
        }
    }
}

const updateParentSkuInventory = async (product, changedParentSku, partnerSettings = { caseSensitive: false }) => {
    let usedParentSku = changedParentSku === "" ? product.parentSku : changedParentSku;
    let originalParentSku = product.parentSku;
    
    // If case sensitivity is off, uppercase the SKUs
    if (!partnerSettings.caseSensitive) {
        usedParentSku = usedParentSku ? usedParentSku.toUpperCase() : usedParentSku;
        originalParentSku = originalParentSku ? originalParentSku.toUpperCase() : originalParentSku;
    }
    
    const runStatus = await utilAddInventory(product.supplierId, usedParentSku, originalParentSku);
    if(runStatus.errorMessage) {
        return {
            success: false,
            data: null,
            error: runStatus.errorMessage
        };
    }
    else {
        return {
            success: true,
            data: null,
            error: null
        }
    }
}

const approveProduct = async (status, changedSku, product, duplicateProduct, inventory, parentSku, partnerSettings = { caseSensitive: false }) => {
    if (product.approvalStatus === "Approved") return {
        success: false,
        data: null,
        error: "Product is already approved!"
    };

    // If case sensitivity is off, uppercase the SKUs
    const processedSku = partnerSettings.caseSensitive ? product.sku : product.sku.toUpperCase();
    const processedParentSku = partnerSettings.caseSensitive ? parentSku : (parentSku || '').toUpperCase();

    if (duplicateProduct) {
        if (inventory) {
            let usedSku = !isNullOrEmpty(inventory.merchantSku) ? inventory.merchantSku : duplicateProduct.Id;
            usedSku = partnerSettings.caseSensitive ? usedSku : (usedSku || '').toUpperCase();
            return await utilApproveProduct(processedSku, product.supplierId, product.supplierName, status, true, usedSku, processedParentSku);
        } else {
            let usedSku = changedSku === "" ? undefined : changedSku;
            if (usedSku) {
                usedSku = partnerSettings.caseSensitive ? usedSku : usedSku.toUpperCase();
            }
            return await utilApproveProduct(processedSku, product.supplierId, product.supplierName, status, true, usedSku, processedParentSku);
        }
    } else {
        if (inventory) {
            let merchantSku = inventory.merchantSku;
            // Treat empty string as no merchantSku
            if (isNullOrEmpty(merchantSku)) {
                merchantSku = undefined;
            } else {
                merchantSku = partnerSettings.caseSensitive ? merchantSku : merchantSku.toUpperCase();
            }
            return await utilApproveProduct(processedSku, product.supplierId, product.supplierName, status, true, merchantSku, processedParentSku);
        } else {
            return await utilApproveProduct(processedSku, product.supplierId, product.supplierName, status, true, undefined, processedParentSku);
        }
    }

}

const approvalCheck = async (product, productInventory, retailerProduct) => {
    const partnerSettings = await utilGetPartnerSettings(product.supplierId);
    return {partnerSettings};
}

export const headlessApprovalFlow = async (product, retailerProduct, productInventory, retailerProductParentSkuMappings, parentSkuMapping, retailerProductMappedSku, andList) => {
    const {partnerSettings} = await approvalCheck(product, productInventory, retailerProduct);
    const syncStatus = andList ? "Listing Pending" : "Not Listed";
    const updatedRetailerProduct = retailerProductMappedSku ? retailerProductMappedSku : retailerProduct;

    // Parent SKU Approval Flow
    let changedParentSku = product.parentSku;
    if(!parentSkuMapping && retailerProductParentSkuMappings) {
        return {
            success: true,
            data: "manual_review",
            error: null
        }
    }
    else if((!parentSkuMapping && !retailerProductParentSkuMappings) && !partnerSettings.autoMatch) {
        await updateParentSkuInventory(product, product.parentSku, partnerSettings);
        changedParentSku = product.parentSku;
    }
    else if (parentSkuMapping) {
        changedParentSku = parentSkuMapping.merchantSku;
    }

    let inventoryRunStatus;
    let approvedProductRunStatus;

    // SKU Approval Flow
    if (updatedRetailerProduct) {
        if (!productInventory) {
            if (partnerSettings.autoMatch) {
                // (Add to retailer catalog with SKU) (No inventory changes)
                approvedProductRunStatus = await approveProduct(syncStatus, undefined, product, updatedRetailerProduct, productInventory, changedParentSku, partnerSettings);
            } else {
                // Ask for option
                //add new merchant sku (Add to retailer catalog with new SKU) (Update inventory new SKU)
                //overwrite existing product (Overwrite retailer product with SKU) (Update inventory SKU)
                return {
                    success: true,
                    data: "manual_review",
                    error: null
                }
            }
        } else {
            // Retailer product exsits with the inventory merchantSku OR no merchant SKU
            if (!productInventory.merchantSku && !partnerSettings.autoMatch) {
                // Ask to overwrite
                //yes (Overwrite retailer product with SKU) (No inventory changes)
                //no (Do nothing)
                return {
                    success: true,
                    data: "manual_review",
                    error: null
                }
            } else {
                // (Add to retailer catalog with SKU) (No inventory changes unless auto match is disabled)
                if (!productInventory.merchantSku && !partnerSettings.autoMatch) {
                    inventoryRunStatus = await updateInventory(product, product.sku, productInventory, partnerSettings);
                }

                // Use the merchant SKU if it exists and is not empty, otherwise use undefined
                const merchantSkuToUse = !isNullOrEmpty(productInventory.merchantSku) ? productInventory.merchantSku : undefined;
                approvedProductRunStatus = await approveProduct(syncStatus, merchantSkuToUse, product, updatedRetailerProduct, productInventory, changedParentSku, partnerSettings);
            }
        }
    } else {
        if (productInventory) {
            let changedSku = ""

            if (!productInventory.merchantSku && !partnerSettings.autoMatch) {
                // (Add to catalog) (Update inventory merchant SKU)
                inventoryRunStatus = await updateInventory(product, product.sku, productInventory, partnerSettings);
                changedSku = undefined
            } else {
                // (Add to retailer catalog with merchant SKU) (No inventory changes)
                changedSku = productInventory.merchantSku;
            }

            approvedProductRunStatus = await approveProduct(syncStatus, changedSku, product, updatedRetailerProduct, productInventory, changedParentSku, partnerSettings);
        } else {
            // (Add to retailer catalog) (No inventory changes)
            if (partnerSettings.autoMatch) {
                approvedProductRunStatus = await approveProduct(syncStatus, undefined, product, updatedRetailerProduct, productInventory, changedParentSku, partnerSettings);
            } else {
                inventoryRunStatus = await updateInventory(product, product.sku, productInventory, partnerSettings);
                approvedProductRunStatus = await approveProduct(syncStatus, undefined, product, updatedRetailerProduct, productInventory, changedParentSku, partnerSettings);
            }
        }
    }

    let inventoryErrorMessage = inventoryRunStatus ? inventoryRunStatus.error ?? "" : "";
    let approvedProductErrorMessage = approvedProductRunStatus ? approvedProductRunStatus.error ?? "" : "";
    let errorMessage =
        inventoryErrorMessage
        + ((inventoryErrorMessage && approvedProductErrorMessage) && " | ") +
        approvedProductErrorMessage;

    let inventorySuccess = inventoryRunStatus ? inventoryRunStatus.success : true;
    let approvedProductSuccess = approvedProductRunStatus ? approvedProductRunStatus.success : true;
    let success = inventorySuccess && approvedProductSuccess;

    return {
        success: success,
        data: null,
        error: errorMessage
    }
}

function ProductApproveDialog(props) {
    const hasInventoryManagePermission = Auth.hasPermission("inventory/manage");

    const globalClasses = useGlobalStyles();

    const {theme, open, setOpen, onApprove} = props;
    const {setUpdating, setError} = props;
    const {approveAndList} = props; // default option to approve and list

    const [loading, setLoading] = React.useState(false);
    const [duplicateProduct, setDuplicateProduct] = React.useState(null);
    const [inventory, setInventory] = React.useState(null);
    const [parentSkuInventory, setParentSkuInventory] = React.useState(null);
    const [existingParentSkuProducts, setExistingParentSkuProducts] = React.useState(null);
    const [contextMessage, setContextMessage] = useState("");
    const [partnerSettings, setPartnerSettings] = useState({caseSensitive: false, autoMatch: false});

    const [changedSku, setChangedSku] = useState("");
    const [changedParentSku, setChangedParentSku] = useState("");
    const [changeSkuDialog, setChangeSkuDialog] = useState(false);
    const [isChangingParentSku, setIsChangingParentSku] = useState(false);

    const updateInventory = async (updateSkuInventory, updateParentSkuInventory) => {
        if (partnerSettings.autoMatch) return;

        // Update inventory for sku
        if (updateSkuInventory) {
            let usedSku = changedSku === "" ? props.product.sku : changedSku;
            let originalSku = props.product.sku;
            
            // If case sensitivity is off, uppercase the SKUs
            if (!partnerSettings.caseSensitive) {
                usedSku = usedSku ? usedSku.toUpperCase() : usedSku;
                originalSku = originalSku ? originalSku.toUpperCase() : originalSku;
            }
            
            const response = await utilAddInventory(props.product.supplierId, usedSku, originalSku, inventory);
            if (response.errorHeader) {
                setError(response);
                return;
            }
        }

        // Update inventory for parent sku
        if(updateParentSkuInventory) {
            if (!parentSkuInventory && props.product.parentSku) {
                let usedParentSku = changedParentSku === "" ? (props.product.parentSku ?? "") : changedParentSku;
                let originalParentSku = props.product.parentSku;
                
                // If case sensitivity is off, uppercase the SKUs
                if (!partnerSettings.caseSensitive) {
                    usedParentSku = usedParentSku ? usedParentSku.toUpperCase() : usedParentSku;
                    originalParentSku = originalParentSku ? originalParentSku.toUpperCase() : originalParentSku;
                }
                
                const response = await utilAddInventory(props.product.supplierId, usedParentSku, originalParentSku);
                if (response.errorHeader) {
                    setError(response);
                    return;
                }
            }
        }
    }
    const approveProduct = async (status) => {
        if (!hasInventoryManagePermission) {
            setError({
                open: true,
                errorHeader: "Permission Denied",
                errorMessage: "You do not have permission to perform this action."
            })
            return;
        }

        if (props.product.approvalStatus === "Approved") {
            return;
        }

        setOpen(false);
        setUpdating(true);

        let runStatus;
        let usedParentSku = changedParentSku === "" ? (parentSkuInventory?.MerchantSKU ?? props.product.parentSku) : changedParentSku;
        let usedSku = changedSku === "" ? (inventory?.MerchantSKU ?? props.product.sku) : changedSku;
        
        // If case sensitivity is off, uppercase the SKUs
        const processedProductSku = partnerSettings.caseSensitive ? props.product.sku : props.product.sku.toUpperCase();
        if (!partnerSettings.caseSensitive) {
            usedParentSku = usedParentSku ? usedParentSku.toUpperCase() : usedParentSku;
            usedSku = usedSku ? usedSku.toUpperCase() : usedSku;
        }

        if (duplicateProduct) {
            if (inventory) {
                runStatus = await utilApproveProduct(processedProductSku, props.product.supplierId, props.product.supplierName, status, true, usedSku, usedParentSku);
            } else {
                runStatus = await utilApproveProduct(processedProductSku, props.product.supplierId, props.product.supplierName, status, true, usedSku, usedParentSku);
            }
        } else {
            if (inventory) {
                const merchantSku = partnerSettings.caseSensitive ? inventory.MerchantSKU : (inventory.MerchantSKU ? inventory.MerchantSKU.toUpperCase() : undefined);
                runStatus = await utilApproveProduct(processedProductSku, props.product.supplierId, props.product.supplierName, status, true, merchantSku, usedParentSku)
            } else {
                runStatus = await utilApproveProduct(processedProductSku, props.product.supplierId, props.product.supplierName, status, true, undefined, usedParentSku);
            }
        }

        if (!runStatus.success) setError({
            open: true,
            errorHeader: "Error Approving Product!",
            errorMessage: runStatus.error
        });
        await new Promise(r => setTimeout(r, 750));
        onApprove(runStatus.success);
        setUpdating(false);
    }
    const updateInventoryAndApprove = async (status, updateSkuInventory, updateParentSkuInventory) => {
        setOpen(false);
        setUpdating(true);
        await updateInventory(updateSkuInventory, updateParentSkuInventory);
        await approveProduct(status);
        setUpdating(false);
    }

    useEffect(() => {
        const getPartnerSettings = async () => {
            try {
                const partnerSettings = await utilGetPartnerSettings(props.product.supplierId);
                setPartnerSettings(partnerSettings);
                return partnerSettings;
            } catch (error) {
                setError({
                    open: true,
                    errorHeader: "Error",
                    errorMessage: "An error occurred while trying to get partner settings."
                });
            }
        }
        const approvalChecks = async (partnerSettings) => {
            let retailerProduct = null;
            let inventory = null;

            try {
                // Check for retailer product
                let foundProduct = await utilGetProduct(props.product.sku, undefined, false, props.isRetailer);
                if (foundProduct.errorStatus === 404) retailerProduct = null;
                else if (foundProduct.errorMessage) throw new Error(foundProduct.errorMessage);
                else retailerProduct = foundProduct;

                // Check for inventory
                const foundInventory = await utilCheckInventory(props.product.supplierId, props.product.sku);
                if (foundInventory.errorStatus === 404) inventory = null;
                else if (foundInventory.errorMessage) throw new Error(foundInventory.errorMessage);
                else inventory = foundInventory;

                // If there is a merchant SKU mapping different from the product SKU, check if that product exists in retailer catalog
                let retailerProductUsingMerchantSku = null;
                let hasDifferingMerchantSku = inventory && (!compareSkus(inventory.MerchantSKU, props.product.sku, partnerSettings.caseSensitive) && !isNullOrEmpty(inventory.MerchantSKU));
                if (inventory && hasDifferingMerchantSku) {
                    const response = await utilGetProduct(inventory.MerchantSKU, undefined, false, props.isRetailer);
                    if (response.errorStatus === 404) { /* Do nothing */ }
                    else if (response.errorMessage) throw new Error(response.errorMessage);
                    else {
                        retailerProductUsingMerchantSku = response;
                    }
                }

                //  Parent SKU checks
                const foundParentSkuInventory = await utilCheckInventory(props.product.supplierId, props.product.parentSku);
                if (!foundParentSkuInventory.errorStatus) {
                    setParentSkuInventory(foundParentSkuInventory);
                } else {
                    const foundParentSkuProducts = await utilSearchRetailerCatalog(`$filter=(parentSku eq '${props.product.parentSku}')`)
                    if (foundParentSkuProducts.data.length > 0) setExistingParentSkuProducts(foundParentSkuProducts.data);
                }

                if(hasDifferingMerchantSku && retailerProductUsingMerchantSku) {
                    setDuplicateProduct(retailerProductUsingMerchantSku);
                } else {
                    setDuplicateProduct(retailerProduct);
                }

                setInventory(inventory);
                

                // Check how to handle approval
                if ((hasDifferingMerchantSku && retailerProductUsingMerchantSku) || (retailerProduct && !hasDifferingMerchantSku)) {
                    setContextMessage(
                        <>
                            {inventory && ((!compareSkus(inventory.MerchantSKU, props.product.sku, partnerSettings.caseSensitive)) && !isNullOrEmpty(inventory.MerchantSKU)) ? (
                                <>
                                    The SKU you’re trying to approve is already linked to a retailer SKU that exists in the retailer’s catalog.
                                    Would you like to overwrite the existing product or enter a different SKU?
                                    <br />
                                    Supplier SKU <b>{props.product.sku}</b> is mapped to Retailer SKU <b>{inventory.MerchantSKU}</b>.

                                    {foundParentSkuInventory && foundParentSkuInventory.MerchantSKU ? (
                                        <>
                                            <br />
                                            Supplier Parent SKU <b>{props.product.parentSku}</b> is mapped to Retailer Parent SKU <b>{foundParentSkuInventory.MerchantSKU}</b>.
                                        </>
                                    ) : <></>}
                                </>
                            ) : (
                                <>
                                    This SKU is already in the retailer's catalog.

                                    <ul>
                                        <li>Select "Overwrite" to replace the existing variant with the new one.</li>
                                        <li>Select "Change SKU" to assign a new retailer SKU to the new variant and
                                            create a separate item in the catalog.</li>
                                    </ul>
                                </>
                            )}
                        </>
                    )
                } else {
                    if (inventory) {
                        if (!inventory.MerchantSKU) {
                            // DO NOTHING
                        } else {
                            // DO NOTHING
                        }
                    } else {
                        // DO NOTHING
                    }
                }

            } catch (error) {
                setError({
                    open: true,
                    errorHeader: "Error",
                    errorMessage: "An error occurred while trying to approve the product."
                });
            }
        }
        const init = async () => {
            setLoading(true);
            const partnerSettings = await getPartnerSettings();
            await approvalChecks(partnerSettings);
            setLoading(false);
        }

        setDuplicateProduct(null);
        setInventory(null);
        setParentSkuInventory(null);
        setExistingParentSkuProducts(null);
        setContextMessage("");
        setPartnerSettings({caseSensitive: false, autoMatch: false});

        setChangedSku("");
        setChangedParentSku("");
        setChangeSkuDialog(false);

        if (props.product === null) return;
        init();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.open]);

    const headerRender = () => {
        if (duplicateProduct !== null) {
            return (
                <>
                    <Typography variant={"h5"}>Existing SKU Found!</Typography>
                    <Typography variant={"body1"}>Would you like to overwrite existing variant or change SKU?</Typography>
                </>
            )
        } else if (!parentSkuInventory && existingParentSkuProducts) {
            return (
                <>
                    <Typography variant={"h5"}>Existing Parent Product found!</Typography>
                    <Typography variant={"body1"}>
                        Your variant's product data includes a parent SKU that matches an existing parent product in the
                        retailer's catalog.
                        <ul style={{margin: 0}}>
                            <li>Select "Add to Existing" to link the new variant to the existing parent product.</li>
                            <li>Select "Create New" to change the variant's parent SKU and create a separate product.</li>
                        </ul>
                    </Typography>
                </>
            )
        } else {
            return (
                <>
                    <Typography variant={"h5"}>Approve Product</Typography>
                    <Typography variant={"body1"} style={{display: "block"}}>
                        Approving SKU(s) will copy their details to the retailer's catalog.
                        <ul>
                            <li>Click "Approve" to add the SKU(s) to the catalog while keeping them in a "Not Listed"
                            status for future listing.</li>
                            <li>Click "Approve & List" to approve the SKU(s) and immediately list them to your eCommerce system.</li>
                        </ul>
                        {contextMessage && <><br/> <br/></>}
                        {contextMessage}
                    </Typography>
                </>
            )
        }
    }

    const bodyRender = () => {
        if (duplicateProduct !== null) {
            return (
                <>
                    <Grid container>
                        <Grid container item xs={12} style={{gap: "5px"}}>
                            <Grid item style={{flex: 1}}>
                                <Typography style={{fontSize: "14px"}}>Existing SKU: {duplicateProduct.Attributes.sku}</Typography>
                                <ProductColumn style={{paddingBottom: "10px"}}
                                               row={{
                                                   sku: duplicateProduct.Attributes.sku,
                                                   image: duplicateProduct.Attributes.image,
                                                   name: duplicateProduct.Attributes.name,
                                                   parentSku: duplicateProduct.Attributes.parent_sku,
                                               }}
                                               column={{id: 0}}
                                               noFilter={true}
                                />
                            </Grid>
                        </Grid>

                        <Grid container item xs={12} style={{marginTop: "0.5em", gap: "5px"}}>
                            <Grid item style={{flex: 1}}>
                                <Typography style={{fontSize: "14px"}}>Newly Introduced SKU: {props.product.sku}</Typography>
                                <ProductColumn style={{paddingBottom: "10px"}}
                                               row={{
                                                   sku: props.product.sku,
                                                   image: props.product.image,
                                                   name: props.product.name,
                                                   parentSku: props.product.parentSku,
                                               }}
                                               column={{id: 0}}
                                               noFilter={true}
                                />
                            </Grid>
                        </Grid>

                        <Typography variant={"body1"} style={{display: "block", marginTop: "0.5em"}}>
                            {contextMessage}
                        </Typography>
                    </Grid>

                    <Grid item container justify={"flex-end"}
                          style={{borderTop: "1px solid #DCDCDC", paddingTop: "12px", gap: "12px"}}>
                        <Button className={globalClasses.button}
                                onClick={() => setOpen(false)}>
                            Cancel
                        </Button>

                        <Button className={clsx(globalClasses.button, globalClasses.outlinedActionButton)}
                                onClick={() => {
                                    const shouldUpdateInventory = !inventory || (inventory && !inventory.MerchantSKU);
                                    const shouldUpdateParentSkuInventory = !parentSkuInventory || (parentSkuInventory && !parentSkuInventory.MerchantSKU);
                                    const status = approveAndList ? "Listing Pending" : "Not Listed";
                                    updateInventoryAndApprove(status, shouldUpdateInventory, shouldUpdateParentSkuInventory);
                                }}>
                            Overwrite
                        </Button>

                        <Button
                            disabled={partnerSettings.autoMatch}
                            className={
                                partnerSettings.autoMatch
                                    ? globalClasses.buttonDisabled
                                    : clsx(globalClasses.button, globalClasses.outlinedActionButton)
                            }
                            onClick={() => {
                                setChangeSkuDialog(true);
                                setChangedParentSku(props.product.parentSku);
                                setChangedSku(props.product.sku + "|" + props.product.supplierId);
                                setIsChangingParentSku(false);
                            }}>
                            Change SKUs
                        </Button>
                    </Grid>
                </>
            );
        }
        else if (!parentSkuInventory && existingParentSkuProducts) {
            return (
                <>
                    <Grid container>
                        <Grid container item xs={12} style={{marginTop: "0.5em", gap: "5px", flexDirection: "column"}}>
                            <Typography style={{fontSize: "14px"}}><b>Parent SKU:</b> {props.product.parentSku}</Typography>

                            <Grid item style={{flex: 1}}>
                                <Typography style={{fontSize: "14px"}}>Current Product: {props.product.sku}</Typography>
                                <ProductColumn style={{paddingBottom: "10px"}}
                                               row={{
                                                   sku: props.product.sku,
                                                   image: props.product.image,
                                                   name: props.product.name,
                                                   parentSku: props.product.parentSku,
                                               }}
                                               column={{id: 0}}
                                               noFilter={true}
                                />
                            </Grid>

                            <Grid container style={{flexDirection: "column"}}>
                                <Typography style={{fontSize: "14px"}}>Found products with same Parent SKU: {props.product.parentSku}</Typography>

                                <Grid container>
                                    {existingParentSkuProducts.map((product, index) => (
                                        <ProductColumn style={{paddingBottom: "10px"}}
                                                       row={{
                                                           sku: product.sku,
                                                           image: product.image,
                                                           name: product.name,
                                                           parentSku: product.parentSku,
                                                       }}
                                                       column={{id: 0}}
                                                       noFilter={true}
                                />
                                    ))}
                                </Grid>
                            </Grid>
                        </Grid>

                        <Typography variant={"body1"}
                                    style={{display: "block", marginTop: "0.5em"}}>
                            {contextMessage}
                        </Typography>
                    </Grid>

                    <Grid item container justify={"flex-end"}
                          style={{borderTop: "1px solid #DCDCDC", paddingTop: "12px", gap: "12px"}}>
                        <Button className={globalClasses.button}
                                onClick={() => setOpen(false)}>
                            Cancel
                        </Button>

                        <Button className={clsx(globalClasses.button, globalClasses.outlinedActionButton)}
                                onClick={() => {
                                    const shouldUpdateInventory = !inventory || (inventory && !inventory.MerchantSKU);
                                    const shouldUpdateParentSkuInventory = !parentSkuInventory || (parentSkuInventory && !parentSkuInventory.MerchantSKU);
                                    const status = approveAndList ? "Listing Pending" : "Not Listed";
                                    updateInventoryAndApprove(status, shouldUpdateInventory, shouldUpdateParentSkuInventory);
                                }}>
                            Add to Existing
                        </Button>

                        {(!inventory || !parentSkuInventory) && (
                            <Button
                                disabled={partnerSettings.autoMatch}
                                className={
                                    partnerSettings.autoMatch
                                        ? globalClasses.buttonDisabled
                                        : clsx(globalClasses.button, globalClasses.outlinedActionButton)
                                }
                                onClick={() => {
                                    setChangeSkuDialog(true);
                                    setChangedParentSku(props.product.parentSku + "|" + props.product.supplierId);
                                    setChangedSku(props.product.sku);
                                    setIsChangingParentSku(true);
                                }}>
                                Create New
                            </Button>
                        )}
                    </Grid>
                </>
            );
        } else {
            return (
                <>
                    <Grid item container justify={"flex-end"}
                          style={{borderTop: "1px solid #DCDCDC", paddingTop: "12px", gap: "12px"}}>
                        <Button className={globalClasses.button}
                                onClick={() => setOpen(false)}>
                            Cancel
                        </Button>

                        <Button
                            className={clsx(globalClasses.button, globalClasses.outlinedActionButton)}
                            onClick={() => {
                                const shouldUpdateInventory = !inventory || (inventory && !inventory.MerchantSKU);
                                const shouldUpdateParentSkuInventory = !parentSkuInventory || (parentSkuInventory && !parentSkuInventory.MerchantSKU);
                                const status = "Not Listed";
                                updateInventoryAndApprove(status, shouldUpdateInventory, shouldUpdateParentSkuInventory);
                            }}>
                            Approve
                        </Button>

                        <Button
                            className={clsx(globalClasses.button, globalClasses.outlinedActionButton)}
                            onClick={() => {
                                const shouldUpdateInventory = !inventory || (inventory && !inventory.MerchantSKU);
                                const shouldUpdateParentSkuInventory = !parentSkuInventory || (parentSkuInventory && !parentSkuInventory.MerchantSKU);
                                const status = "Listing Pending";
                                updateInventoryAndApprove(status, shouldUpdateInventory, shouldUpdateParentSkuInventory);
                            }}>
                            Approve & List
                        </Button>
                    </Grid>
                </>
            );
        }
    }

    if (props.product === null) return <></>;
    return (
        <Dialog open={open} onClose={() => setOpen(false)}>
            <div style={{
                height: "10px",
                width: "100%",
                background: (duplicateProduct !== null || (!parentSkuInventory && existingParentSkuProducts)) ? theme.palette.error.main : "#22b36e",
                transition: "background 0.5s ease"
            }}/>

            <Grid container style={{padding: "12px", gap: "12px"}}>
                <Grid item container alignItems={"center"}>
                    <Grid item container style={{flex: 1, flexDirection: "column"}}>
                        {headerRender()}
                    </Grid>
                </Grid>

                {loading ? (
                    <ContentLoader preserveAspectRatio='none'
                                   style={{height: '2em', width: '100%'}}
                                   primaryColor='#e1e1e1'
                                   secondaryColor='#d8d8d8'/>
                ) : (
                    <>
                        {bodyRender()}
                    </>
                )}

            </Grid>

            {changeSkuDialog && (
                <Dialog open={changeSkuDialog} onClose={() => setChangeSkuDialog(false)}>
                    <div style={{
                        height: "10px",
                        width: "100%",
                        background: duplicateProduct !== null ? theme.palette.error.main : "#22b36e",
                        transition: "background 0.5s ease"
                    }}/>

                    <Grid container style={{padding: "12px", gap: "12px"}}>
                        <Grid item container alignItems={"center"}>
                            <Grid item container style={{flex: 1, flexDirection: "column"}}>
                                <Typography variant={"h5"}>
                                    {isChangingParentSku
                                        ? "Create New Product"
                                        : "Change SKU"
                                    }
                                </Typography>
                            </Grid>

                            {!isChangingParentSku && (
                                <Grid container style={{marginTop: "1em"}}>
                                    <EnhancedTextField label={"Merchant SKU"}
                                                       value={changedSku}
                                                       onChange={(e) => {
                                                           setChangedSku(e.target.value)
                                                       }}
                                    />
                                </Grid>
                            )}

                            {isChangingParentSku && (
                                <Grid container style={{marginTop: "1em"}}>
                                    <EnhancedTextField label={"Merchant Parent SKU"}
                                                       value={changedParentSku}
                                                       onChange={(e) => {
                                                           setChangedParentSku(e.target.value)
                                                       }}
                                    />
                                </Grid>
                            )}
                        </Grid>

                        <Grid item container justify={"flex-end"}
                              style={{borderTop: "1px solid #DCDCDC", paddingTop: "12px", gap: "12px"}}>
                            <Button className={globalClasses.button}
                                    onClick={() => {
                                        setChangeSkuDialog(false);
                                        setChangedSku("");
                                        setChangedParentSku("");
                                    }}>
                                Cancel
                            </Button>

                            <Button className={clsx(globalClasses.button, globalClasses.outlinedActionButton)}
                                    onClick={() => {
                                        updateInventoryAndApprove(approveAndList ? "Listing Pending" : "Not Listed", !compareSkus(changedSku, props.product.sku, partnerSettings.caseSensitive), !compareSkus(changedParentSku, props.product.parentSku, partnerSettings.caseSensitive));
                                    }}>
                                Update
                            </Button>
                        </Grid>
                    </Grid>
                </Dialog>
            )}
        </Dialog>
    )
}

export default withTheme(ProductApproveDialog);
